import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { applyAuthInterceptors } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import {
  Sparkles, Upload, Loader2, Cpu, Heading1, Pilcrow, Table2, Plus, Trash2,
  ArrowUp, ArrowDown, Download, BookmarkPlus, ChevronDown, FileText, X, FolderOpen, Image as ImageIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = applyAuthInterceptors(axios.create({ baseURL: API }));

const AI_MODELS = [
  { value: "gpt-4o", label: "GPT-4o — highest quality" },
  { value: "gpt-4o-mini", label: "GPT-4o mini — faster & cheaper" },
];

const newBlock = (type) => {
  if (type === "heading") return { type: "heading", text: "Section Heading", level: 1 };
  if (type === "paragraph") return { type: "paragraph", text: "" };
  if (type === "image") return { type: "image", image_id: null, url: null, size: "medium", align: "center" };
  return { type: "table", headers: ["Column 1", "Column 2"], rows: [["", ""]] };
};

export default function DocumentBuilder() {
  const [docName, setDocName] = useState("Untitled Document");
  const [blocks, setBlocks] = useState([]);
  const [useLetterhead, setUseLetterhead] = useState(true);
  const [instruction, setInstruction] = useState("");
  const [aiModel, setAiModel] = useState("gpt-4o");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [usage, setUsage] = useState(null);
  const [templates, setTemplates] = useState([]);
  const fileRef = useRef();

  const loadTemplates = () => api.get("/builder/templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { loadTemplates(); }, []);

  // ---- block ops ----
  const updateBlock = (i, patch) => setBlocks((bs) => bs.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const addBlock = (type) => setBlocks((bs) => [...bs, newBlock(type)]);
  const delBlock = (i) => setBlocks((bs) => bs.filter((_, idx) => idx !== i));
  const moveBlock = (i, dir) => setBlocks((bs) => {
    const j = i + dir;
    if (j < 0 || j >= bs.length) return bs;
    const copy = [...bs];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  // table ops
  const setCell = (bi, ri, ci, v) => setBlocks((bs) => bs.map((b, idx) => {
    if (idx !== bi) return b;
    const rows = b.rows.map((r) => [...r]);
    rows[ri][ci] = v;
    return { ...b, rows };
  }));
  const setHeader = (bi, ci, v) => setBlocks((bs) => bs.map((b, idx) => {
    if (idx !== bi) return b;
    const headers = [...b.headers]; headers[ci] = v;
    return { ...b, headers };
  }));
  const addRow = (bi) => setBlocks((bs) => bs.map((b, idx) => (idx === bi ? { ...b, rows: [...b.rows, b.headers.map(() => "")] } : b)));
  const delRow = (bi, ri) => setBlocks((bs) => bs.map((b, idx) => (idx === bi ? { ...b, rows: b.rows.filter((_, i) => i !== ri) } : b)));
  const addCol = (bi) => setBlocks((bs) => bs.map((b, idx) => (idx === bi ? { ...b, headers: [...b.headers, `Column ${b.headers.length + 1}`], rows: b.rows.map((r) => [...r, ""]) } : b)));
  const delCol = (bi, ci) => setBlocks((bs) => bs.map((b, idx) => {
    if (idx !== bi || b.headers.length <= 1) return b;
    return { ...b, headers: b.headers.filter((_, i) => i !== ci), rows: b.rows.map((r) => r.filter((_, i) => i !== ci)) };
  }));

  // image block upload
  const uploadImage = async (bi, file) => {
    if (!file) return;
    updateBlock(bi, { uploading: true });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/builder/image", fd);
      const url = `${API}/builder/images/${r.data.image_id}`;
      updateBlock(bi, { image_id: r.data.image_id, url, uploading: false });
      toast.success("Image added");
    } catch {
      updateBlock(bi, { uploading: false });
      toast.error("Could not upload image");
    }
  };

  // ---- files ----
  const onFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    setFiles((f) => [...f, ...chosen]);
    e.target.value = "";
  };
  const removeFile = (i) => setFiles((f) => f.filter((_, idx) => idx !== i));

  // ---- AI ----
  const generate = async () => {
    if (files.length === 0 && !instruction.trim()) {
      return toast.error("Add reference files or write an instruction first");
    }
    setLoading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (instruction.trim()) fd.append("instruction", instruction);
      fd.append("model", aiModel);
      const r = await api.post("/builder/analyze", fd);
      setDocName(r.data.name || "Untitled Document");
      setBlocks(r.data.blocks || []);
      setUsage(r.data.ai_usage || null);
      toast.success("AI drafted your document — edit it below");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generation failed");
    } finally {
      setLoading(false);
    }
  };

  // ---- export / save ----
  const exportPdf = async () => {
    if (blocks.length === 0) return toast.error("Add some content first");
    setExporting(true);
    try {
      const r = await api.post("/builder/export-pdf", { name: docName, blocks, use_letterhead: useLetterhead }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${docName || "document"}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF exported");
    } catch {
      toast.error("Could not export PDF");
    } finally {
      setExporting(false);
    }
  };

  const saveTemplate = async () => {
    if (blocks.length === 0) return toast.error("Add some content first");
    try {
      await api.post("/builder/templates", { name: docName, blocks });
      toast.success("Saved to your templates");
      loadTemplates();
    } catch {
      toast.error("Could not save template");
    }
  };

  const loadTemplate = (t) => {
    setDocName(t.name);
    setBlocks(JSON.parse(JSON.stringify(t.blocks || [])));
    setUsage(null);
    toast.success(`Loaded "${t.name}"`);
  };

  const deleteTemplate = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/builder/templates/${id}`);
    toast.success("Template deleted");
    loadTemplates();
  };

  return (
    <div className="px-4 lg:px-8 py-6 lg:py-8">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="font-head font-extrabold text-2xl tracking-tight flex items-center gap-2" data-testid="builder-title">
            <Sparkles className="h-6 w-6 text-amber-500" /> AI Document Builder
          </h1>
          <p className="text-sm text-slate-500 mt-1">Upload references, let AI draft it, then fine-tune every heading, paragraph and table.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-lg" data-testid="load-template-btn">
                <FolderOpen className="h-4 w-4 mr-1.5" /> Templates {templates.length ? `(${templates.length})` : ""} <ChevronDown className="h-3.5 w-3.5 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 max-h-[340px] overflow-y-auto">
              {templates.length === 0 && <div className="px-2 py-3 text-xs text-slate-400 text-center">No saved templates yet</div>}
              {templates.map((t) => (
                <DropdownMenuItem key={t.id} onSelect={(e) => { e.preventDefault(); loadTemplate(t); }} className="gap-2 cursor-pointer justify-between" data-testid={`builder-tpl-${t.id}`}>
                  <span className="text-sm truncate">{t.name}</span>
                  <button onClick={(e) => deleteTemplate(t.id, e)} className="text-slate-300 hover:text-red-600 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" className="rounded-lg" onClick={saveTemplate} data-testid="save-template-btn">
            <BookmarkPlus className="h-4 w-4 mr-1.5" /> Save as Template
          </Button>
          <Button className="rounded-lg bg-slate-900 hover:bg-slate-800" onClick={exportPdf} disabled={exporting} data-testid="export-pdf-btn">
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Export PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* LEFT: AI panel */}
        <div className="xl:col-span-4">
          <Card className="p-5 rounded-xl border-border bg-slate-900 text-white sticky top-6">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-400" />
              <h3 className="font-head font-bold">Generate with AI</h3>
            </div>
            <p className="text-xs text-slate-300 mt-2 leading-relaxed">
              Upload reference files (PDF, Word, images or screenshots) and/or describe what you need. GPT-4o drafts a
              structured document you can edit.
            </p>

            <div className="mt-4">
              <label className="flex items-center gap-1.5 text-[11px] text-slate-400 mb-1"><Cpu className="h-3.5 w-3.5" /> AI model</label>
              <select value={aiModel} onChange={(e) => setAiModel(e.target.value)} className="w-full rounded-lg bg-white/5 border border-white/10 text-white text-xs px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-400" data-testid="builder-model-select">
                {AI_MODELS.map((m) => <option key={m.value} value={m.value} className="text-slate-900">{m.label}</option>)}
              </select>
            </div>

            <input ref={fileRef} type="file" multiple accept=".pdf,.txt,.docx,.png,.jpg,.jpeg,.webp" hidden onChange={onFiles} data-testid="builder-file-input" />
            <Button onClick={() => fileRef.current?.click()} className="w-full mt-4 rounded-lg bg-white/10 hover:bg-white/20 border-0 text-white" data-testid="builder-upload-btn">
              <Upload className="h-4 w-4 mr-2" /> Add reference files
            </Button>
            {files.length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid="builder-file-list">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs bg-white/5 rounded-lg px-2.5 py-1.5">
                    <FileText className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                    <span className="truncate flex-1">{f.name}</span>
                    <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}

            <div className="text-center text-[11px] text-slate-400 my-3">and / or describe it</div>
            <Textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={4} placeholder="e.g. Draft a company profile with a products table and certifications section…" className="resize-none bg-white/5 border-white/10 text-white placeholder:text-slate-500 text-xs" data-testid="builder-instruction-input" />

            <Button onClick={generate} disabled={loading} className="w-full mt-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 border-0" data-testid="builder-generate-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {loading ? "Drafting…" : "Generate Document"}
            </Button>
            {usage && (
              <div className="mt-3 text-[11px] text-amber-300 flex items-center gap-1.5" data-testid="builder-cost-meter">
                <Cpu className="h-3 w-3" /> {usage.model} · {usage.total_tokens.toLocaleString()} tok · ~${usage.cost_usd < 0.01 ? usage.cost_usd.toFixed(4) : usage.cost_usd.toFixed(3)}
              </div>
            )}
          </Card>
        </div>

        {/* RIGHT: editor */}
        <div className="xl:col-span-8">
          <Card className="p-5 rounded-xl border-border">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <Input value={docName} onChange={(e) => setDocName(e.target.value)} className="font-head font-bold text-lg max-w-md border-0 border-b rounded-none px-0 focus-visible:ring-0" data-testid="builder-doc-name" />
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <Switch checked={useLetterhead} onCheckedChange={setUseLetterhead} data-testid="builder-letterhead-toggle" />
                On company letterhead
              </label>
            </div>

            {blocks.length === 0 ? (
              <div className="text-center py-16 text-slate-400" data-testid="builder-empty">
                <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Generate a draft with AI, or add blocks manually to start building.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="builder-blocks">
                {blocks.map((b, i) => (
                  <div key={i} className="group relative rounded-lg border border-slate-200 hover:border-slate-300 p-3 pl-10 transition-colors" data-testid={`builder-block-${i}`}>
                    <div className="absolute left-1.5 top-2 flex flex-col gap-0.5 opacity-40 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => moveBlock(i, -1)} className="text-slate-400 hover:text-slate-900" data-testid={`block-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                      <button onClick={() => moveBlock(i, 1)} className="text-slate-400 hover:text-slate-900" data-testid={`block-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                      <button onClick={() => delBlock(i)} className="text-slate-400 hover:text-red-600" data-testid={`block-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>

                    {b.type === "heading" && (
                      <div className="flex items-center gap-2">
                        <select value={b.level} onChange={(e) => updateBlock(i, { level: Number(e.target.value) })} className="text-[11px] rounded border border-slate-200 px-1.5 py-1 text-slate-500" data-testid={`heading-level-${i}`}>
                          <option value={1}>H1</option>
                          <option value={2}>H2</option>
                        </select>
                        <Input value={b.text} onChange={(e) => updateBlock(i, { text: e.target.value })} placeholder="Heading text" className={`border-0 px-0 focus-visible:ring-0 font-head font-bold ${b.level === 1 ? "text-xl" : "text-base"}`} data-testid={`heading-input-${i}`} />
                      </div>
                    )}

                    {b.type === "paragraph" && (
                      <Textarea value={b.text} onChange={(e) => updateBlock(i, { text: e.target.value })} placeholder="Write a paragraph…" rows={3} className="resize-y border-0 px-0 focus-visible:ring-0 text-sm leading-relaxed" data-testid={`paragraph-input-${i}`} />
                    )}

                    {b.type === "table" && (
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-xs">
                          <thead>
                            <tr>
                              {b.headers.map((h, ci) => (
                                <th key={ci} className="border border-slate-200 bg-slate-900 p-0 relative">
                                  <input value={h} onChange={(e) => setHeader(i, ci, e.target.value)} className="w-full bg-transparent text-white font-semibold px-2 py-1.5 focus:outline-none placeholder:text-slate-400" placeholder={`Column ${ci + 1}`} data-testid={`table-${i}-header-${ci}`} />
                                  {b.headers.length > 1 && (
                                    <button onClick={() => delCol(i, ci)} className="absolute -top-2 -right-1 bg-white border border-slate-300 rounded-full text-red-500 opacity-0 group-hover:opacity-100 hover:bg-red-50" title="Delete column"><X className="h-3 w-3" /></button>
                                  )}
                                </th>
                              ))}
                              <th className="w-8 border-0 p-0">
                                <button onClick={() => addCol(i)} className="text-slate-400 hover:text-emerald-600 px-1" title="Add column" data-testid={`table-${i}-add-col`}><Plus className="h-4 w-4" /></button>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {b.rows.map((row, ri) => (
                              <tr key={ri}>
                                {row.map((cell, ci) => (
                                  <td key={ci} className="border border-slate-200 p-0">
                                    <input value={cell} onChange={(e) => setCell(i, ri, ci, e.target.value)} className="w-full px-2 py-1.5 focus:outline-none focus:bg-emerald-50" data-testid={`table-${i}-cell-${ri}-${ci}`} />
                                  </td>
                                ))}
                                <td className="w-8 border-0 text-center">
                                  <button onClick={() => delRow(i, ri)} className="text-slate-300 hover:text-red-600" title="Delete row"><Trash2 className="h-3.5 w-3.5" /></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <button onClick={() => addRow(i)} className="mt-2 text-xs text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1" data-testid={`table-${i}-add-row`}><Plus className="h-3.5 w-3.5" /> Add row</button>
                      </div>
                    )}

                    {b.type === "image" && (
                      <div data-testid={`image-block-${i}`}>
                        {b.url ? (
                          <div className={`flex ${b.align === "left" ? "justify-start" : b.align === "right" ? "justify-end" : "justify-center"}`}>
                            <img src={b.url} alt="" className={`rounded-lg border border-slate-200 ${b.size === "small" ? "max-w-[160px]" : b.size === "large" ? "max-w-[480px]" : "max-w-[300px]"} w-full object-contain`} data-testid={`image-preview-${i}`} />
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center gap-2 py-8 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors text-slate-400">
                            {b.uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImageIcon className="h-6 w-6" />}
                            <span className="text-xs">{b.uploading ? "Uploading…" : "Click to upload a logo or photo"}</span>
                            <input type="file" accept=".png,.jpg,.jpeg,.webp,.gif" hidden onChange={(e) => { uploadImage(i, e.target.files?.[0]); e.target.value = ""; }} data-testid={`image-upload-${i}`} />
                          </label>
                        )}
                        {b.url && (
                          <div className="flex items-center gap-3 mt-2">
                            <select value={b.size} onChange={(e) => updateBlock(i, { size: e.target.value })} className="text-[11px] rounded border border-slate-200 px-1.5 py-1 text-slate-500" data-testid={`image-size-${i}`}>
                              <option value="small">Small</option>
                              <option value="medium">Medium</option>
                              <option value="large">Large</option>
                            </select>
                            <select value={b.align} onChange={(e) => updateBlock(i, { align: e.target.value })} className="text-[11px] rounded border border-slate-200 px-1.5 py-1 text-slate-500" data-testid={`image-align-${i}`}>
                              <option value="left">Left</option>
                              <option value="center">Center</option>
                              <option value="right">Right</option>
                            </select>
                            <label className="text-[11px] text-emerald-700 hover:text-emerald-800 cursor-pointer inline-flex items-center gap-1">
                              <Upload className="h-3 w-3" /> Replace
                              <input type="file" accept=".png,.jpg,.jpeg,.webp,.gif" hidden onChange={(e) => { uploadImage(i, e.target.files?.[0]); e.target.value = ""; }} />
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add block toolbar */}
            <div className="flex items-center gap-2 mt-5 pt-4 border-t border-slate-100 flex-wrap">
              <span className="text-xs text-slate-400 mr-1">Add block:</span>
              <Button size="sm" variant="outline" className="rounded-lg" onClick={() => addBlock("heading")} data-testid="add-heading-btn"><Heading1 className="h-4 w-4 mr-1.5" /> Heading</Button>
              <Button size="sm" variant="outline" className="rounded-lg" onClick={() => addBlock("paragraph")} data-testid="add-paragraph-btn"><Pilcrow className="h-4 w-4 mr-1.5" /> Paragraph</Button>
              <Button size="sm" variant="outline" className="rounded-lg" onClick={() => addBlock("table")} data-testid="add-table-btn"><Table2 className="h-4 w-4 mr-1.5" /> Table</Button>
              <Button size="sm" variant="outline" className="rounded-lg" onClick={() => addBlock("image")} data-testid="add-image-btn"><ImageIcon className="h-4 w-4 mr-1.5" /> Image</Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
